/* eslint-disable no-console */
import { BROWSER } from 'esm-env';
import { parse } from './icu-messageformat-parser/index.js';
import * as IntlMessageFormat from 'intl-messageformat';
import { dayjs } from './dayjs.js';
import { Language } from './languages.js';
import { isErrorWithMessage, isLoadOptionsBrowser, isLoadOptionsRemote, isTType, } from './types.js';
import { cachedFetch, EtagCache } from './etagCache.js';
import { debug, debugOptions } from './logging.js';
const CACHE = new EtagCache();
const TRANSLATIONS = new Map();
const getTranslations = () => {
    if (BROWSER) {
        return window.translations;
    }
    return TRANSLATIONS;
};
const setTranslations = (trans) => {
    if (BROWSER) {
        window.translations = trans;
    }
    trans.forEach((value, key) => {
        TRANSLATIONS.set(key, value);
    });
};
const hasTranslations = (lang) => {
    if (BROWSER) {
        return Boolean(window.translations && window.translations.size);
    }
    return Boolean(TRANSLATIONS.has(lang));
};
export const clearTranslations = () => {
    TRANSLATIONS.clear();
    CACHE.clearCahe();
};
/**
 * Alternative function to l function that you can use by your preprocessor and replace it to support live editing.
 * @param language
 * @param key
 * @param values
 */
export const _l = (language, key, values) => {
    const text = l(language, key, values);
    if (BROWSER) {
        if (window.translationsLive) {
            return `
        <span data-translation-key="${key}" class="translation-live">
          <span title="${key}" onclick="window.dispatchEvent(new CustomEvent('translation-edit', {detail: {key: '${key}', text: '${text.replaceAll('"', '|')}', values: '${values ? JSON.stringify(values).replaceAll('"', '|') : '{}'}' , element: this}}));return false;"></span>
          <span>${text}</span>
        </span>`;
        }
        if (window.translationsTest) {
            return `<span data-translation-key="${key}">${text}</span>`;
        }
    }
    return text;
};
function getAst(language, key) {
    const dictionary = getTranslations()?.get(language?.toLowerCase());
    if (!dictionary) {
        debug(`Translating key "${key}"`);
        debug(`Failed to get dictionary for language "${language}"!`, console.error);
        return key;
    }
    return dictionary.get(key);
}
export function keyExists(language, key) {
    return Boolean(getAst(language, key));
}
export const l = (language, key, values) => {
    if (isTType(key)) {
        values = key.values;
        key = key.key;
    }
    if (BROWSER && window.translationsShowKeys) {
        return key;
    }
    let ast = getAst(language, key);
    if (!ast) {
        debug(`missing translation for key "${key}", language "${language}"`, console.warn);
        return key;
    }
    try {
        if (typeof ast === 'string') {
            ast = parse(ast, { ignoreTag: true });
        }
    }
    catch (e) {
        console.error('translations:l', `Could not parse key "${key}", language "${language}"`, {
            originalMessage: e.originalMessage,
        });
        return e.originalMessage ?? key;
    }
    try {
        return String(new IntlMessageFormat.IntlMessageFormat(ast, language).format(values));
    }
    catch (e) {
        console.error('translations:l', `Could not format key "${key}"`, { language, ast });
        return key;
    }
};
export const defaultLoadOptions = {
    minCacheTimeInSeconds: 60 * 10,
};
/**
 * Initialize translations
 *
 * @param options
 * @param options.language
 * @param options.fetch custom fetch function for server side rendering, must be used in combination with serverUrl
 * @param options.serverUrl server url for fetching translations for client side rendering and server side rendering using fetch function
 * @param options.importFolder used for server side rendering when translations are imported from folder (usually baked during build time)
 * @param options.subset set the subset of used translation, if not provided, defaults to `all`
 * @param options.loadAllLanguages whether to load all languages during initialization
 * @param options.enableTranslationsLive set true to enable live editing of translations
 * @param options.minCacheTimeInSeconds used when fetching translations from server using custom provided fetch function, applicable for server side rendering. Defaults to 10 minutes
 * @param options.debug set true to enable debug logging, should not be used on production
 * @param options.strict if true, the initialization fails in case of failed loading of translations
 *
 * @returns Returns promise that resolves when translations are loaded. Promise returns function that can translate keys for given language.
 */
export async function initTranslations(options) {
    debugOptions.debug = options.debug || false;
    options = { ...defaultLoadOptions, ...options };
    const _language = options.language?.toLowerCase();
    dayjs.locale(_language);
    if (!hasTranslations(_language)) {
        if (!isLanguage(_language)) {
            throw new Error(`Unsupported language "${_language}"!`);
        }
        if (BROWSER)
            window.translations = new Map();
        if (options.loadAllLanguages) {
            setTranslations(await loadAllTranslations(options));
        }
        else {
            getTranslations().set(_language, await loadStringsForLanguage(options));
        }
        if (BROWSER) {
            window.language = _language;
            if (options.enableTranslationsLive) {
                setGlobalVariable('translationsLive', 'translations');
            }
            setGlobalVariable('translationsTest');
            setGlobalVariable('translationsShowKeys');
        }
    }
    else if (!BROWSER && isLoadOptionsRemote(options)) {
        revalidateTranslationsInBackground(options);
    }
    return (key, values) => {
        return l(_language, key, values);
    };
}
async function revalidateTranslationsInBackground(options) {
    debug(`Going to revalidate translations on background`);
    getTranslations().set(options.language, await loadStringsForLanguageRemote(options));
}
const setGlobalVariable = (key, keyAlias) => {
    if (typeof localStorage !== 'undefined') {
        const searchParams = new URLSearchParams(window.location.search);
        const value = searchParams.get(key) ?? (keyAlias ? searchParams.get(keyAlias) : null);
        if (value !== null) {
            // @ts-ignore
            window[key] = value === 'true';
            localStorage.setItem(key, value);
        }
        if (localStorage.getItem(key) === 'true') {
            // @ts-ignore
            window[key] = true;
        }
    }
};
export const getLanguageUrl = (serverUrl, language, subset) => `${serverUrl}/${language}/${subset ? subset + '.json' : 'all.json'}`;
function isLanguage(language) {
    return Boolean(Language[language]);
}
async function loadStringsForLanguage(options) {
    let data = new Map();
    if (BROWSER && isLoadOptionsBrowser(options)) {
        debug('initializing in browser');
        data = await loadStringsForLanguageBrowser(options);
    }
    else if (isLoadOptionsRemote(options)) {
        debug('initializing from serverUrl');
        data = await loadStringsForLanguageRemote(options);
    }
    else {
        throw new Error('Invalid load options for initTranslations!');
    }
    return data;
}
async function loadStringsForLanguageBrowser(options) {
    const { serverUrl, language, subset } = options;
    const url = getLanguageUrl(serverUrl, language, subset);
    try {
        const response = await fetch(url, {
            method: 'GET',
            headers: {
                accept: 'application/json',
            },
        });
        return await transformResponse(options)(response);
    }
    catch (e) {
        return errorFallback(options, isErrorWithMessage(e) ? e.message : undefined);
    }
}
function errorFallback(options, message) {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { fetchFunction, ...loggableOptions } = options;
    console.error(`Failed to download translations. Message: ${message}, options: ${JSON.stringify(loggableOptions)}`);
    if (options.strict) {
        throw new Error(`Failed to download translations. Message: ${message}`);
    }
    return new Map();
}
async function loadStringsForLanguageRemote(options) {
    const { serverUrl, language, subset, fetchFunction, minCacheTimeInSeconds } = options;
    const url = getLanguageUrl(serverUrl, language, subset);
    try {
        return await cachedFetch(fetchFunction, CACHE, minCacheTimeInSeconds, transformResponse(options))(url, {
            method: 'GET',
            headers: {
                accept: 'application/json',
            },
        });
    }
    catch (e) {
        return errorFallback(options, isErrorWithMessage(e) ? e.message : undefined);
    }
}
const transformResponse = (options) => async (response) => {
    if (!response.ok) {
        return errorFallback(options, `Status retrieved: ${response.status}`);
    }
    const json = await response.json();
    return new Map(Object.entries(json));
};
async function loadAllTranslations(options) {
    const dictionaries = new Map();
    debug('loading all translations');
    const languages = Object.values(Language);
    for (const language of languages) {
        const translations = await loadStringsForLanguage({ ...options, language });
        dictionaries.set(language, translations);
    }
    return dictionaries;
}
export const getTranslationsList = (language, key, limit) => {
    return Array.from({ length: limit }, (_, i) => `${key}.${i + 1}`).filter(k => {
        const text = l(language, k);
        return text !== k && typeof text === 'string' && text.length > 0;
    });
};
/* eslint-enable no-console */
